import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../services/auth';

interface DocRow {
  orderId: string;
  orderNumber?: string | null;
  reference?: string | null;
  date: string;
  status: string;
  isPaid: boolean;
  vatMode?: string | null;
  legalName: string;
  edrpou: string;
  net: number;
  vat: number;
  gross: number;
  currency: string;
  hasResult: boolean;
}

interface DocsResponse {
  total: number;
  totalNet: number;
  totalVat: number;
  totalGross: number;
  rows: DocRow[];
}

const money = (n: number) => `${n.toFixed(2)}`;
const fmtDate = (s: string) => {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('uk-UA');
};

export const AdminDocumentsPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [data, setData] = useState<DocsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [vatMode, setVatMode] = useState('');
  const [paid, setPaid] = useState('');
  const [search, setSearch] = useState('');

  // Client-side text search over contractor name / ЄДРПОУ (instant; date/seller/
  // paid stay server-side). Footer totals are recomputed from the visible rows.
  const rows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter(
      (r) => (r.legalName || '').toLowerCase().includes(q) || (r.edrpou || '').toLowerCase().includes(q),
    );
  }, [data, search]);
  const totals = useMemo(
    () => ({
      net: rows.reduce((s, r) => s + r.net, 0),
      vat: rows.reduce((s, r) => s + r.vat, 0),
      gross: rows.reduce((s, r) => s + r.gross, 0),
    }),
    [rows],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      if (vatMode) qs.set('vatMode', vatMode);
      if (paid) qs.set('paid', paid);
      const res = await authFetch(`/api/admin/documents?${qs.toString()}`);
      if (!res.ok) throw new Error(String(res.status));
      setData(await res.json());
    } catch {
      setError(t('admin:docsLoadError'));
    } finally {
      setLoading(false);
    }
  }, [from, to, vatMode, paid, t]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadDoc = async (orderId: string, kind: 'invoice' | 'act' | 'result') => {
    setBusy(`${orderId}:${kind}`);
    try {
      const res = await authFetch(`/api/admin/orders/${orderId}/${kind}`);
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const b = await res.json(); if (b?.message) msg = b.message; } catch { /* ignore */ }
        throw new Error(msg);
      }
      const { url } = await res.json();
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setBusy(null);
    }
  };

  const exportCsv = () => {
    if (!data) return;
    const head = ['№', t('admin:docDate'), t('admin:docContractor'), t('admin:docEdrpou'), t('admin:docSeller'), t('admin:docNet'), t('admin:docVat'), t('admin:docGross'), t('admin:docPaid')];
    const lines = rows.map((r) => [
      r.orderNumber || r.reference || r.orderId,
      fmtDate(r.date),
      `"${(r.legalName || '').replace(/"/g, '""')}"`,
      r.edrpou,
      r.vatMode === 'vat' ? 'ТОВ (з ПДВ)' : 'ФОП (без ПДВ)',
      money(r.net), money(r.vat), money(r.gross),
      r.isPaid ? '1' : '0',
    ].join(','));
    const csv = [head.join(','), ...lines].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `documents-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const inputCls = 'rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-400';

  return (
    <div className="admin-page od-modern">
      <div className="admin-header">
        <div>
          <h1>{t('admin:docsTitle')}</h1>
          <p className="admin-subtitle">{t('admin:docsSubtitle')}</p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-slate-100 bg-white p-4">
        <label className="flex flex-col gap-1 text-xs text-slate-500">{t('admin:docFrom')}
          <input type="date" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">{t('admin:docTo')}
          <input type="date" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">{t('admin:docSeller')}
          <select className={inputCls} value={vatMode} onChange={(e) => setVatMode(e.target.value)}>
            <option value="">{t('admin:docAll')}</option>
            <option value="vat">ТОВ (з ПДВ)</option>
            <option value="novat">ФОП (без ПДВ)</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">{t('admin:docPaid')}
          <select className={inputCls} value={paid} onChange={(e) => setPaid(e.target.value)}>
            <option value="">{t('admin:docAll')}</option>
            <option value="paid">{t('admin:docPaidYes')}</option>
            <option value="unpaid">{t('admin:docPaidNo')}</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">{t('admin:docSearch')}
          <input
            type="search"
            className={`${inputCls} min-w-[220px]`}
            placeholder={t('admin:docSearchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <button type="button" className="ml-auto rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50" onClick={exportCsv} disabled={!data || data.rows.length === 0}>
          ⬇ {t('admin:docExportCsv')}
        </button>
      </div>

      {loading && <p className="text-sm text-slate-500">{t('common:loading')}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!loading && data && (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2.5">№</th>
                <th className="px-3 py-2.5">{t('admin:docDate')}</th>
                <th className="px-3 py-2.5">{t('admin:docContractor')}</th>
                <th className="px-3 py-2.5">{t('admin:docEdrpou')}</th>
                <th className="px-3 py-2.5">{t('admin:docSeller')}</th>
                <th className="px-3 py-2.5 text-right">{t('admin:docNet')}</th>
                <th className="px-3 py-2.5 text-right">{t('admin:docVat')}</th>
                <th className="px-3 py-2.5 text-right">{t('admin:docGross')}</th>
                <th className="px-3 py-2.5 text-center">{t('admin:docPaid')}</th>
                <th className="px-3 py-2.5 text-center">{t('admin:b2bDownloadInvoice')}</th>
                <th className="px-3 py-2.5 text-center">{t('admin:b2bDownloadAct')}</th>
                <th className="px-3 py-2.5 text-center">{t('admin:docResult')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.orderId} className="border-b border-slate-50 text-slate-700 hover:bg-slate-50/60">
                  <td className="px-3 py-2.5 font-medium text-slate-900">{r.orderNumber || r.reference || '—'}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-3 py-2.5">{r.legalName || '—'}</td>
                  <td className="px-3 py-2.5">{r.edrpou || '—'}</td>
                  <td className="px-3 py-2.5">
                    <span className={`rounded px-1.5 py-0.5 text-xs ${r.vatMode === 'vat' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {r.vatMode === 'vat' ? 'ТОВ' : 'ФОП'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">{money(r.net)}</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">{r.vat > 0 ? money(r.vat) : '—'}</td>
                  <td className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">{money(r.gross)}</td>
                  <td className="px-3 py-2.5 text-center">{r.isPaid ? '✅' : '⏳'}</td>
                  <td className="px-3 py-2.5 text-center">
                    <button type="button" className="text-blue-600 hover:underline disabled:opacity-50" disabled={busy === `${r.orderId}:invoice`} onClick={() => downloadDoc(r.orderId, 'invoice')} title={t('admin:b2bDownloadInvoice')}>📄</button>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {r.status?.toLowerCase() === 'completed'
                      ? <button type="button" className="text-blue-600 hover:underline disabled:opacity-50" disabled={busy === `${r.orderId}:act`} onClick={() => downloadDoc(r.orderId, 'act')} title={t('admin:b2bDownloadAct')}>📃</button>
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {r.hasResult
                      ? <button type="button" className="text-blue-600 hover:underline disabled:opacity-50" disabled={busy === `${r.orderId}:result`} onClick={() => downloadDoc(r.orderId, 'result')} title={t('admin:docResult')}>🧪</button>
                      : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={12} className="px-3 py-8 text-center text-slate-400">{t('admin:docsEmpty')}</td></tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="border-t border-slate-200 font-semibold text-slate-900">
                  <td className="px-3 py-2.5" colSpan={5}>{t('admin:docTotal')} ({rows.length})</td>
                  <td className="px-3 py-2.5 text-right">{money(totals.net)}</td>
                  <td className="px-3 py-2.5 text-right">{money(totals.vat)}</td>
                  <td className="px-3 py-2.5 text-right">{money(totals.gross)}</td>
                  <td colSpan={4}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};
